import type { FilterState } from "./filters.js";
import { emptyFilters } from "./filters.js";
import type { TabKey } from "../components/Shell.js";

export interface UrlState {
  tab: TabKey;
  snapId: number | null;
  filters: FilterState;
  sev: string;
  rule: string;
}

const TAB_KEYS: TabKey[] = [
  "overview", "worklist", "newrate", "estimation", "components",
  "data", "crosscheck", "trend", "pic", "export", "mapping",
];

function parseSet(v: string | null): Set<string> {
  if (!v) return new Set();
  return new Set(v.split(",").map((s) => s.trim()).filter(Boolean));
}

/** Read the whole view out of the query string. Unknown values fall back. */
export function readUrl(search: string = window.location.search): UrlState {
  const q = new URLSearchParams(search);
  const tabRaw = q.get("tab");
  const tab = TAB_KEYS.includes(tabRaw as TabKey) ? (tabRaw as TabKey) : "overview";

  const snapRaw = Number(q.get("snap"));
  const snapId = Number.isInteger(snapRaw) && snapRaw > 0 ? snapRaw : null;

  const filters: FilterState = {
    ...emptyFilters(),
    comps: parseSet(q.get("comp")),
    cats: parseSet(q.get("cat")),
    region: q.get("region") ?? "",
    status: q.get("status") ?? "",
    rateType: q.get("rate") ?? "",
    pic: q.get("pic") ?? "",
    search: q.get("q") ?? "",
    includeCatE: q.get("cate") === "1",
  };

  return { tab, snapId, filters, sev: q.get("sev") ?? "", rule: q.get("check") ?? "" };
}

/** Serialise the view; defaults are omitted so shared links stay readable. */
export function writeUrl(s: UrlState): string {
  const q = new URLSearchParams();
  if (s.tab !== "overview") q.set("tab", s.tab);
  if (s.snapId !== null) q.set("snap", String(s.snapId));
  if (s.filters.comps.size) q.set("comp", [...s.filters.comps].join(","));
  if (s.filters.cats.size) q.set("cat", [...s.filters.cats].join(","));
  if (s.filters.region) q.set("region", s.filters.region);
  if (s.filters.status) q.set("status", s.filters.status);
  if (s.filters.rateType) q.set("rate", s.filters.rateType);
  if (s.filters.pic) q.set("pic", s.filters.pic);
  if (s.filters.search.trim()) q.set("q", s.filters.search.trim());
  if (s.filters.includeCatE) q.set("cate", "1");
  if (s.sev) q.set("sev", s.sev);
  if (s.rule) q.set("check", s.rule);
  const str = q.toString();
  return str ? `?${str}` : window.location.pathname;
}
